// gRPC-Web client factory for the CUSTOMER portal (ADR-179 P2). Reuses the
// SAME generated ConsoleServiceClient class and transport contract as the
// operator console (src/lib/grpcweb/client.ts) — the customer RPCs are new
// methods on the same ConsoleService (ADR-179 D4: "Reuse... the generated
// ConsoleServiceClientPb") — but is a DELIBERATELY SEPARATE module with its
// own client singleton and its own auth-state wiring (customer_session /
// customer_csrf via lib/auth/session.ts), never the operator's
// lib/auth/session.ts. This is the load-bearing isolation ADR-179 D4 calls
// for: "it can ONLY call customerAllowedMethods, its auth-context only
// understands getCustomer(), and it has NO code path that lists across
// accounts." A shared transport singleton would risk an operator API key
// or console_session leaking onto a customer-portal call (or vice versa);
// two independent singletons make that structurally impossible.
import * as grpcWeb from 'grpc-web'
import { ConsoleServiceClient } from '@/generated/ConsoleServiceClientPb'
import {
  clearCustomerSession,
  getCustomerSessionCsrf,
} from '@/lib/auth/session'

// Same VITE_CONSOLE_GRPC_WEB_URL escape hatch as the operator client — see
// that module's comment. Empty string resolves every RPC relative to the
// page's own origin (same-origin Ingress path-split, ADR-118 Phase 5C).
const GRPC_WEB_URL = import.meta.env.VITE_CONSOLE_GRPC_WEB_URL || ''

let customerClient: ConsoleServiceClient | null = null

function getCustomerClient(): ConsoleServiceClient {
  if (!customerClient) {
    // withCredentials: true so the browser attaches the HttpOnly
    // customer_session cookie on every call — the customer portal has NO
    // bearer-key mode (unlike the operator surface), cookie auth is the
    // only path, so this is unconditionally required rather than a
    // no-op-in-key-mode convenience.
    customerClient = new ConsoleServiceClient(GRPC_WEB_URL, null, { withCredentials: true })
  }
  return customerClient
}

// Every customer-portal RPC used by this app's pages is a read (List*/Get*/
// WhoAmI/DownloadMyLicense — a read of signed license material, not a
// mutation) except ChangeMyPassword, which mirrors the operator's
// change-password shape: session+CSRF gated. The customer allowlist
// (services/console/server's customerAllowedMethods, ADR-179 D2.3 layer 1)
// is the SERVER-side authority on what a customer_session cookie can call
// at all; this table only decides whether THIS client attaches
// X-CSRF-Token, mirroring the server's `!isViewerSafeMethod` CSRF gate the
// same way the operator client.ts does.
//
// ADR-179 P3 additions: inviteMember/removeMember/setMemberRole/
// requestSeatChange/requestCancellation/listMyOrgBillingPortals all mirror
// customerViewerSafeMethods=false on the server (server/customer_identity.go)
// — see that file's doc for why listMyOrgBillingPortals in particular is
// classified unsafe despite reading a Stripe-hosted resource (it triggers
// a live Stripe API call per invocation). listMembers is the one new
// method left OUT of this set — it mirrors the server's true (read-only).
const customerMutatingMethods: ReadonlySet<string> = new Set([
  'changeMyPassword',
  'inviteMember',
  'removeMember',
  'setMemberRole',
  'requestSeatChange',
  'requestCancellation',
  'listMyOrgBillingPortals',
  // ADR-186 'Get help' -- MISSED when RequestSupport shipped (live-caught
  // 2026-08-31: the server's CSRF gate denied every drawer submit with
  // PermissionDenied because this client-side twin of the server's
  // customerViewerSafeMethods classification was never updated). These two
  // lists MUST move together whenever a customer mutation is added.
  'requestSupport',
])

function isCsrfRequiredMethod(methodName: keyof ConsoleServiceClient): boolean {
  return customerMutatingMethods.has(String(methodName))
}

let onCustomerUnauthenticated: (() => void) | null = null

export function setCustomerUnauthenticatedHandler(handler: () => void): void {
  onCustomerUnauthenticated = handler
}

/**
 * callConsoleCustomer invokes a unary ConsoleServiceClient method as the
 * CUSTOMER principal (cookie mode only), centralizing the 401 ->
 * clear-session-and-redirect reaction exactly as the operator's
 * callConsole does for its own session type. methodName must be the exact
 * ConsoleServiceClient method name.
 */
export async function callConsoleCustomer<Req, Res>(
  methodName: keyof ConsoleServiceClient,
  request: Req,
): Promise<Res> {
  const metadata = buildCustomerMetadata(methodName)

  const rpc = getCustomerClient()[methodName] as unknown as (
    req: Req,
    md: grpcWeb.Metadata,
  ) => Promise<Res>

  try {
    return await rpc.call(getCustomerClient(), request, metadata)
  } catch (err) {
    const rpcErr = err as grpcWeb.RpcError
    if (rpcErr && rpcErr.code === grpcWeb.StatusCode.UNAUTHENTICATED) {
      clearCustomerSession()
      onCustomerUnauthenticated?.()
    }
    throw err
  }
}

function buildCustomerMetadata(methodName: keyof ConsoleServiceClient): grpcWeb.Metadata {
  if (isCsrfRequiredMethod(methodName)) {
    const csrf = getCustomerSessionCsrf()
    if (csrf) return { 'X-CSRF-Token': csrf }
  }
  return {}
}

// callConsoleCustomerUnauthenticated invokes acceptInvite — the ONE
// customer-portal RPC with NO session cookie at all (server/interceptors.go's
// unauthenticatedMethods, not customerAllowedMethods; see that file's doc).
// Deliberately does NOT go through callConsoleCustomer: there is no cookie
// to attach (withCredentials on the shared client is harmless here — no
// customer_session cookie exists yet — but there is also no CSRF token to
// send, and a failure here must NOT trigger the "clear session and
// redirect to login" reaction callConsoleCustomer performs on
// UNAUTHENTICATED, since there was never a session to clear in the first
// place).
export async function callConsoleCustomerUnauthenticated<Req, Res>(
  methodName: keyof ConsoleServiceClient,
  request: Req,
): Promise<Res> {
  const rpc = getCustomerClient()[methodName] as unknown as (
    req: Req,
    md: grpcWeb.Metadata,
  ) => Promise<Res>
  return rpc.call(getCustomerClient(), request, {})
}
